import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { MeResponse } from '@lk/shared';
import { fetchMe, loginPin, loginParent, logout } from '../../lib/api';
import { rememberTrustedProfile, forgetTrustedProfile } from '../../lib/device';
import { purgeUserCaches } from '../../lib/queryClient';
import { getSyncer } from '../../offline/runtime';
import { loginWithPasskey } from './webauthn';

export const ME_KEY = ['me'] as const;

export function useMe(): UseQueryResult<MeResponse | null> {
  return useQuery<MeResponse | null>({
    queryKey: ME_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Чия зараз черга офлайну.
 *
 * Дитина входить у власний профіль — `user.learnerId` заповнений. Батько
 * входить у кабінет і дитиною не є: для нього власника немає, тож черга
 * має стояти порожньою, а не підхоплювати рядки останньої дитини.
 * `me.learners[0]` тут навмисно НЕ використовуємо, хоч так роблять екрани
 * дитини: для батька це була б довільна з двох дітей.
 */
function activeLearnerOf(me: MeResponse): string | null {
  return me.user.learnerId ?? null;
}

export function usePinLogin(): ReturnType<
  typeof useMutation<MeResponse, Error, { pin: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pin: string }) => loginPin(input),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
      rememberTrustedProfile(me.user.id);
      // Черга могла зупинитись на 401 — після входу одразу доганяємо.
      // learnerId передаємо ОБОВʼЯЗКОВО: без нього власник черги лишається
      // тим, хто грав минулого разу, і перший же flush відправить чужі
      // відповіді під цією сесією. Батько learnerId не має — тоді null,
      // і черга не візьме нічого, поки не зайде дитина.
      getSyncer().resumeAfterLogin(activeLearnerOf(me));
    },
  });
}

export function useParentLogin(): ReturnType<
  typeof useMutation<MeResponse, Error, { email: string; password: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => loginParent(input),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
      rememberTrustedProfile(me.user.id);
      getSyncer().resumeAfterLogin(activeLearnerOf(me));
    },
  });
}

/**
 * Вхід за passkey. Хук потрібен саме тому, що після входу мало отримати
 * MeResponse — його треба покласти в кеш `me`, інакше RequireAuth ще нічого
 * не знає про сесію і відкидає назад на екран входу.
 */
export function usePasskeyLogin(): ReturnType<typeof useMutation<MeResponse, Error, void>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => loginWithPasskey(),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
      rememberTrustedProfile(me.user.id);
      getSyncer().resumeAfterLogin(activeLearnerOf(me));
    },
  });
}

export function useLogout(): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => logout(),
    onSettled: async () => {
      forgetTrustedProfile();
      // Кеш серверного стану зносимо ПОВНІСТЮ — і з памʼяті, і з IndexedDB,
      // і з Cache Storage. Позначити запити застарілими мало: доки летить
      // новий запит, екран малюється старими даними, і наступний користувач
      // побачив би залишки попереднього. Офлайн-чергу — НІКОЛИ:
      // там лежать відповіді дитини, які ще не доїхали.
      await purgeUserCaches(queryClient);
      // Явний null (а не порожньо) — щоб RequireAuth одразу знав: сесії немає,
      // і не тримав дитину на екрані завантаження до відповіді сервера.
      queryClient.setQueryData(ME_KEY, null);
    },
  });
}
